import { useState } from "react"
import { useTranslation } from "react-i18next"
import { Mail, Plus, Pencil, Trash2, RefreshCw, CheckCircle2, XCircle, Wifi } from "lucide-react"
import {
  useEmailAccounts,
  useCreateEmailAccount,
  useUpdateEmailAccount,
  useDeleteEmailAccount,
  useTestEmailConnection,
} from "@/hooks/use-email-accounts"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog"
import { toast } from "sonner"
import type { EmailAccount, EmailProvider } from "@/lib/types"

// ── Provider Config ──────────────────────────────────────────────

const PROVIDER_DEFAULTS: Record<string, { imap_host: string; imap_port: number; smtp_host: string; smtp_port: number }> = {
  google: { imap_host: "imap.gmail.com", imap_port: 993, smtp_host: "smtp.gmail.com", smtp_port: 587 },
  microsoft: { imap_host: "outlook.office365.com", imap_port: 993, smtp_host: "smtp.office365.com", smtp_port: 587 },
}

const PROVIDER_LABELS: Record<string, string> = {
  custom: "Własny serwer",
  google: "Google (Gmail)",
  microsoft: "Microsoft 365",
}

// ── Account Form Dialog ──────────────────────────────────────────

interface AccountFormData {
  email: string
  display_name: string
  provider: EmailProvider
  imap_host: string
  imap_port: number
  imap_ssl: boolean
  imap_username: string
  imap_password: string
  smtp_host: string
  smtp_port: number
  smtp_tls: boolean
  smtp_username: string
  smtp_password: string
  sync_enabled: boolean
  signature_html: string
}

const EMPTY_FORM: AccountFormData = {
  email: "",
  display_name: "",
  provider: "custom",
  imap_host: "",
  imap_port: 993,
  imap_ssl: true,
  imap_username: "",
  imap_password: "",
  smtp_host: "",
  smtp_port: 587,
  smtp_tls: true,
  smtp_username: "",
  smtp_password: "",
  sync_enabled: true,
  signature_html: "",
}

function AccountFormDialog({
  open,
  account,
  onClose,
}: {
  open: boolean
  account: EmailAccount | null
  onClose: () => void
}) {
  const createMutation = useCreateEmailAccount()
  const updateMutation = useUpdateEmailAccount()
  const isEdit = !!account

  const [form, setForm] = useState<AccountFormData>(() => {
    if (account) {
      return {
        email: account.email,
        display_name: account.display_name || "",
        provider: account.provider,
        imap_host: account.imap_host || "",
        imap_port: account.imap_port,
        imap_ssl: account.imap_ssl,
        imap_username: "",
        imap_password: "",
        smtp_host: account.smtp_host || "",
        smtp_port: account.smtp_port,
        smtp_tls: account.smtp_tls,
        smtp_username: "",
        smtp_password: "",
        sync_enabled: account.sync_enabled,
        signature_html: account.signature_html || "",
      }
    }
    return { ...EMPTY_FORM }
  })

  const handleProviderChange = (provider: EmailProvider) => {
    const defaults = PROVIDER_DEFAULTS[provider]
    if (defaults) {
      setForm((f) => ({
        ...f,
        provider,
        imap_host: defaults.imap_host,
        imap_port: defaults.imap_port,
        smtp_host: defaults.smtp_host,
        smtp_port: defaults.smtp_port,
      }))
    } else {
      setForm((f) => ({ ...f, provider }))
    }
  }

  const handleSubmit = () => {
    if (!form.email) {
      toast.error("Adres email jest wymagany")
      return
    }

    const data: Record<string, unknown> = {
      email: form.email,
      display_name: form.display_name || null,
      provider: form.provider,
      auth_type: "password",
      imap_host: form.imap_host,
      imap_port: form.imap_port,
      imap_ssl: form.imap_ssl,
      smtp_host: form.smtp_host,
      smtp_port: form.smtp_port,
      smtp_tls: form.smtp_tls,
      sync_enabled: form.sync_enabled,
      signature_html: form.signature_html || null,
    }

    // Only include credentials if provided
    if (form.imap_username) data.imap_username = form.imap_username
    if (form.imap_password) data.imap_password_encrypted = form.imap_password
    if (form.smtp_username) data.smtp_username = form.smtp_username
    if (form.smtp_password) data.smtp_password_encrypted = form.smtp_password

    if (isEdit) {
      updateMutation.mutate(
        { id: account.id, data },
        {
          onSuccess: () => {
            toast.success("Konto zaktualizowane")
            onClose()
          },
          onError: () => toast.error("Nie udało się zaktualizować konta"),
        }
      )
    } else {
      createMutation.mutate(data, {
        onSuccess: () => {
          toast.success("Konto email dodane")
          onClose()
        },
        onError: () => toast.error("Nie udało się dodać konta"),
      })
    }
  }

  const isPending = createMutation.isPending || updateMutation.isPending

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-[560px] max-h-[85vh] overflow-auto">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? "Edytuj konto email" : "Dodaj konto email"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Provider */}
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">
              Dostawca
            </label>
            <div className="flex gap-2">
              {(["custom", "google", "microsoft"] as EmailProvider[]).map((p) => (
                <button
                  key={p}
                  onClick={() => handleProviderChange(p)}
                  className={`rounded-md border px-3 py-1.5 text-sm transition-colors ${
                    form.provider === p
                      ? "border-primary bg-primary/[0.08] text-primary"
                      : "border-border text-muted-foreground"
                  }`}
                >
                  <span>
                    {PROVIDER_LABELS[p]}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Email + Display Name */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">
                Adres email *
              </label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                className="w-full rounded-md border border-border bg-muted px-3 py-2 text-sm text-foreground"
                placeholder="jan@firma.pl"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">
                Nazwa wyświetlana
              </label>
              <input
                value={form.display_name}
                onChange={(e) => setForm((f) => ({ ...f, display_name: e.target.value }))}
                className="w-full rounded-md border border-border bg-muted px-3 py-2 text-sm text-foreground"
                placeholder="Jan Kowalski"
              />
            </div>
          </div>

          {/* IMAP Settings */}
          <fieldset className="rounded-lg border p-3">
            <legend className="px-2 text-xs font-semibold text-muted-foreground">
              Serwer poczty przychodzącej (IMAP)
            </legend>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <label className="mb-1 block text-xs text-muted-foreground">Host</label>
                <input
                  value={form.imap_host}
                  onChange={(e) => setForm((f) => ({ ...f, imap_host: e.target.value }))}
                  className="w-full rounded-md border border-border bg-muted px-3 py-1.5 text-sm text-foreground"
                  placeholder="imap.firma.pl"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs text-muted-foreground">Port</label>
                <input
                  type="number"
                  value={form.imap_port}
                  onChange={(e) => setForm((f) => ({ ...f, imap_port: parseInt(e.target.value) || 993 }))}
                  className="w-full rounded-md border border-border bg-muted px-3 py-1.5 text-sm text-foreground"
                />
              </div>
            </div>
            <div className="mt-2 grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-xs text-muted-foreground">Login</label>
                <input
                  value={form.imap_username}
                  onChange={(e) => setForm((f) => ({ ...f, imap_username: e.target.value }))}
                  className="w-full rounded-md border border-border bg-muted px-3 py-1.5 text-sm text-foreground"
                  placeholder={isEdit ? "••••••" : "jan@firma.pl"}
                />
              </div>
              <div>
                <label className="mb-1 block text-xs text-muted-foreground">Hasło</label>
                <input
                  type="password"
                  value={form.imap_password}
                  onChange={(e) => setForm((f) => ({ ...f, imap_password: e.target.value }))}
                  className="w-full rounded-md border border-border bg-muted px-3 py-1.5 text-sm text-foreground"
                  placeholder={isEdit ? "••••••" : ""}
                />
              </div>
            </div>
            <div className="mt-2">
              <label className="flex items-center gap-2 text-xs text-muted-foreground">
                <input
                  type="checkbox"
                  checked={form.imap_ssl}
                  onChange={(e) => setForm((f) => ({ ...f, imap_ssl: e.target.checked }))}
                />
                SSL/TLS
              </label>
            </div>
          </fieldset>

          {/* SMTP Settings */}
          <fieldset className="rounded-lg border p-3">
            <legend className="px-2 text-xs font-semibold text-muted-foreground">
              Serwer poczty wychodzącej (SMTP)
            </legend>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <label className="mb-1 block text-xs text-muted-foreground">Host</label>
                <input
                  value={form.smtp_host}
                  onChange={(e) => setForm((f) => ({ ...f, smtp_host: e.target.value }))}
                  className="w-full rounded-md border border-border bg-muted px-3 py-1.5 text-sm text-foreground"
                  placeholder="smtp.firma.pl"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs text-muted-foreground">Port</label>
                <input
                  type="number"
                  value={form.smtp_port}
                  onChange={(e) => setForm((f) => ({ ...f, smtp_port: parseInt(e.target.value) || 587 }))}
                  className="w-full rounded-md border border-border bg-muted px-3 py-1.5 text-sm text-foreground"
                />
              </div>
            </div>
            <div className="mt-2 grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-xs text-muted-foreground">Login</label>
                <input
                  value={form.smtp_username}
                  onChange={(e) => setForm((f) => ({ ...f, smtp_username: e.target.value }))}
                  className="w-full rounded-md border border-border bg-muted px-3 py-1.5 text-sm text-foreground"
                  placeholder={isEdit ? "••••••" : "jan@firma.pl"}
                />
              </div>
              <div>
                <label className="mb-1 block text-xs text-muted-foreground">Hasło</label>
                <input
                  type="password"
                  value={form.smtp_password}
                  onChange={(e) => setForm((f) => ({ ...f, smtp_password: e.target.value }))}
                  className="w-full rounded-md border border-border bg-muted px-3 py-1.5 text-sm text-foreground"
                  placeholder={isEdit ? "••••••" : ""}
                />
              </div>
            </div>
            <div className="mt-2">
              <label className="flex items-center gap-2 text-xs text-muted-foreground">
                <input
                  type="checkbox"
                  checked={form.smtp_tls}
                  onChange={(e) => setForm((f) => ({ ...f, smtp_tls: e.target.checked }))}
                />
                STARTTLS
              </label>
            </div>
          </fieldset>

          {/* Sync toggle */}
          <label className="flex items-center gap-2 text-sm text-foreground">
            <input
              type="checkbox"
              checked={form.sync_enabled}
              onChange={(e) => setForm((f) => ({ ...f, sync_enabled: e.target.checked }))}
            />
            Synchronizuj pocztę przychodzącą
          </label>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            Anuluj
          </Button>
          <Button onClick={handleSubmit} disabled={isPending}>
            {isPending ? "Zapisywanie..." : isEdit ? "Zapisz" : "Dodaj konto"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ── Main Page ────────────────────────────────────────────────────

export function EmailAccountsPage() {
  const { t } = useTranslation()
  const { data, isLoading } = useEmailAccounts()
  const deleteMutation = useDeleteEmailAccount()
  const testMutation = useTestEmailConnection()
  const [formOpen, setFormOpen] = useState(false)
  const [editAccount, setEditAccount] = useState<EmailAccount | null>(null)
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null)

  const accounts = data?.data || []

  const handleDelete = (id: string) => {
    deleteMutation.mutate(id, {
      onSuccess: () => {
        toast.success("Konto usunięte")
        setDeleteConfirm(null)
      },
      onError: () => toast.error("Nie udało się usunąć konta"),
    })
  }

  const handleTest = (id: string) => {
    testMutation.mutate(id, {
      onSuccess: (res) => {
        if (res.status === "ok") {
          toast.success("Połączenie działa poprawnie")
        } else {
          toast.error(res.message || "Błąd połączenia")
        }
      },
      onError: () => toast.error("Nie udało się przetestować połączenia"),
    })
  }

  return (
    <div className="max-w-3xl">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="page-title">
            Konta email
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Zarządzaj kontami pocztowymi połączonymi z CRM
          </p>
        </div>
        <Button
          onClick={() => {
            setEditAccount(null)
            setFormOpen(true)
          }}
          size="sm"
        >
          <Plus size={14} className="mr-1" /> Dodaj konto
        </Button>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2].map((i) => (
            <Skeleton key={i} className="h-20 w-full rounded-lg" />
          ))}
        </div>
      ) : accounts.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border py-16">
          <Mail size={40} className="mb-3 opacity-20 text-muted-foreground" />
          <p className="text-sm font-medium text-foreground">
            Brak kont email
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            Dodaj konto email, aby synchronizować pocztę z CRM
          </p>
          <Button
            onClick={() => {
              setEditAccount(null)
              setFormOpen(true)
            }}
            size="sm"
            className="mt-4"
          >
            <Plus size={14} className="mr-1" /> Dodaj konto
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {accounts.map((account) => (
            <div
              key={account.id}
              className="flex items-center gap-4 rounded-lg border p-4"
            >
              <div
                className="flex h-10 w-10 items-center justify-center rounded-full"
                style={{
                  backgroundColor: account.provider === "google" ? "#EA433520" : account.provider === "microsoft" ? "#0078D420" : "rgba(59,130,246,0.1)",
                }}
              >
                <Mail
                  size={18}
                  style={{
                    color: account.provider === "google" ? "#EA4335" : account.provider === "microsoft" ? "#0078D4" : undefined,
                  }}
                  className={account.provider === "custom" ? "text-primary" : ""}
                />
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium truncate text-foreground">
                    {account.email}
                  </span>
                  <Badge
                    variant={account.is_active ? "default" : "secondary"}
                    className="text-[10px]"
                  >
                    {account.is_active ? "Aktywne" : "Nieaktywne"}
                  </Badge>
                  {account.provider !== "custom" && (
                    <span className="text-[10px] text-muted-foreground">
                      {PROVIDER_LABELS[account.provider]}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3 mt-1">
                  {account.display_name && (
                    <span className="text-xs text-muted-foreground">
                      {account.display_name}
                    </span>
                  )}
                  {account.last_sync_at && (
                    <span className="text-xs text-muted-foreground">
                      Ost. sync: {new Date(account.last_sync_at).toLocaleString("pl-PL")}
                    </span>
                  )}
                  {account.last_sync_status && (
                    <span className="flex items-center gap-1 text-xs">
                      {account.last_sync_status === "success" ? (
                        <CheckCircle2 size={10} className="text-green-500" />
                      ) : account.last_sync_status === "error" ? (
                        <XCircle size={10} className="text-destructive" />
                      ) : (
                        <RefreshCw size={10} className="animate-spin text-primary" />
                      )}
                    </span>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-1">
                <button
                  onClick={() => handleTest(account.id)}
                  className="flex h-7 w-7 items-center justify-center rounded-md transition-colors hover:bg-muted text-muted-foreground"
                  title="Testuj połączenie"
                  disabled={testMutation.isPending}
                >
                  <Wifi size={14} />
                </button>
                <button
                  onClick={() => {
                    setEditAccount(account)
                    setFormOpen(true)
                  }}
                  className="flex h-7 w-7 items-center justify-center rounded-md transition-colors hover:bg-muted text-muted-foreground"
                  title="Edytuj"
                >
                  <Pencil size={14} />
                </button>
                <button
                  onClick={() => setDeleteConfirm(account.id)}
                  className="flex h-7 w-7 items-center justify-center rounded-md transition-colors hover:bg-muted text-destructive"
                  title="Usuń"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Form Dialog */}
      {formOpen && (
        <AccountFormDialog
          open={formOpen}
          account={editAccount}
          onClose={() => {
            setFormOpen(false)
            setEditAccount(null)
          }}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={(v) => !v && setDeleteConfirm(null)}>
        <DialogContent className="max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Usuń konto email</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Czy na pewno chcesz usunąć to konto email? Ta operacja jest nieodwracalna.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>
              Anuluj
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteConfirm && handleDelete(deleteConfirm)}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? "Usuwanie..." : "Usuń"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
